import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuth } from './lib/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import BookSessionModal from './BookSessionModal';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import 'react-toastify/dist/ReactToastify.css';



export default function Calendar() {
  const { accessToken: token, user } = useAuth();
  const [initialView, setInitialView] = useState("dayGridMonth");
  const navigate = useNavigate(); // Initialize navigate

  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    setInitialView(isMobile ? "timeGridWeek" : "dayGridMonth");
  }, []);
  
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

   
  useEffect(() => {
    if (!token) return;

    fetch('https://quirkleai.com/api/sessions/', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load sessions');
        return res.json();
      })
      .then(data => {
        const formatted = data.map(s => {
          const therapistName = s.therapist?.username || 'Therapist';
          const clientName = s.client?.username || 'Client';

          let title = `Session ${s.id}`;
          if (user?.username === therapistName) {
            title = `With ${clientName}`;
          } else if (user?.username === clientName) {
            title = `With Dr. ${therapistName}`;
          }

          return {
            id: s.id,
            title,
            start: s.start_time,
            end: s.end_time,
            extendedProps: {
              therapist: s.therapist,
              client: s.client,
              meeting_id: s.meeting_id, // Include meeting_id here
            },
          };
        });

        setEvents(formatted);
      })
      .catch(err => {
        console.error(err);
        toast.error('Error loading sessions');
      });
  }, [token, user]);

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (clickedDate < today) {
      toast.warn('This is a past date. You cannot book a new session.');
      return;
    }

    setSelectedDate(info.dateStr);
    setShowModal(true);
  };

  const handleBookingSubmit = ({ start, end, client_id, therapist_id }) => {
    const payload = {
      start_time: start,
      end_time: end,
      ...(user.role === 'therapist' ? { client: client_id } : {}),
      ...(user.role === 'client' ? { therapist: therapist_id } : {}),
    };

    fetch('https://quirkleai.com/api/sessions/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to book session');
        toast.success('Session booked!');
        setShowModal(false);
        return res.json();
      })
      .then(newSession => {
        const formattedEvent = {
          id: newSession.id,
          title: `With ${newSession.client.username}`,
          start: newSession.start_time,
          end: newSession.end_time,
          extendedProps: {
            therapist: newSession.therapist,
            client: newSession.client,
            meeting_id: newSession.meeting_id,
          },
        };
        setEvents(prevEvents => [...prevEvents, formattedEvent]);
      })
      .catch(err => {
        toast.error('Booking failed.');
        console.error(err);
      });
  };

  const handleEventClick = (info) => {
    const now = new Date();
    const eventStart = new Date(info.event.start);
    const eventEnd = new Date(info.event.end);

    const therapistUsername = info.event.extendedProps?.therapist?.username;
    const clientUsername = info.event.extendedProps?.client?.username;
    const meetingId = info.event.extendedProps?.meeting_id; // Get meeting_id

    const isTherapist = user?.username === therapistUsername;
    const isClient = user?.username === clientUsername;

    if (!isTherapist && !isClient) {
      toast.error('You are not a participant in this session.');
      return;
    }

    if (eventEnd < now) {
      toast.warn('This session is already over.');
      return;
    }

    if (eventStart > now) {
      toast.info('You can only join when the session starts.');
      return;
    }

    if (!meetingId) {
      toast.error('Meeting ID is missing for this session.');
      return;
    }

    // Navigate to the Jitsi room URL
    navigate(`/therapist/jitsi-room/${meetingId}`);
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="w-full overflow-x-auto">
        <div className="min-w-[500px] sm:min-w-full">
          <FullCalendar
          
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            // initialView="timeGridWeek"
            initialView={initialView}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            events={events}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            height="auto"
            timeZone="local"
            dayMaxEvents={3}
            eventDisplay="block"
            contentHeight="auto"
            eventClassNames={() => [
              '!bg-indigo-700',
              '!text-white',
              'rounded-md',
              'px-2',
              'py-0.5',
              'text-xs',
              'leading-tight',
              'font-medium',
              'whitespace-nowrap',
              'overflow-hidden',
              'text-ellipsis',
            ]}
          />
        </div>
      </div>

      {showModal && (
        <BookSessionModal
          token={token}
          date={selectedDate}
          onClose={() => setShowModal(false)}
          onSubmit={handleBookingSubmit}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
